import {
  createAssistantStream,
  type AssistantStream,
  type AssistantStreamController,
} from 'assistant-stream';
import { truncate } from 'lodash';

import { useAssistantCloudStore } from './stores/assistant-cloud-store';
import { ensureStoreInitialized } from './utils';

export class RunsAPI {
  async stream(body: {
    thread_id: string;
    assistant_id: 'system/thread_title';
    messages: readonly unknown[];
  }): Promise<AssistantStream> {
    const { thread_id, assistant_id, messages } = body;
    return assistant_id === 'system/thread_title'
      ? this.generateTitle(thread_id, messages)
      : createAssistantStream(async (controller: AssistantStreamController) => controller.close());
  }

  async report(_data: unknown): Promise<void> {
    // No-op: local runtime doesn't report telemetry to the cloud
  }

  private async generateTitle(
    threadId: string,
    messages: readonly unknown[],
  ): Promise<AssistantStream> {
    await ensureStoreInitialized();
    const { updateThread } = useAssistantCloudStore.getState();
    const title = this.extractTitleFromMessages(messages);

    await updateThread(threadId, { title }).catch(() => {
      // Thread title update failed - thread may not exist yet
    });

    return createAssistantStream(async (controller: AssistantStreamController) => {
      controller.appendText(title);
      controller.close();
    });
  }

  private extractTitleFromMessages(messages: readonly unknown[]): string {
    const firstUserMessage = messages.find(
      (m: unknown) => (m as Record<string, unknown>).role === 'user',
    );

    if (!firstUserMessage) {
      return 'New Thread';
    }

    const content = (firstUserMessage as Record<string, unknown>).content;

    if (typeof content === 'string') {
      return truncate(content, { length: 50, omission: '' });
    }

    if (Array.isArray(content)) {
      const textPart = content.find(
        (part: unknown) => (part as Record<string, unknown>).type === 'text',
      );
      const text = (textPart as Record<string, unknown>)?.text;
      if (typeof text === 'string') {
        return truncate(text, { length: 50, omission: '' });
      }
    }

    return 'New Thread';
  }
}
